<template>
	<div class="phone-number-selector-wrapper">
		<!-- Label -->
        <label-generic :text="selectorLabel"/>
		<div class="selected-number-container" :class="{'remove-border': !selectedPhoneNumber, 'mobile': updateCustomerImageUrl}">
            <!-- Selected Number -->
			<div class="selected-number">
                {{selectedPhoneNumber ? selectedPhoneNumber.contact : t('emptyFieldState')}}
            </div>
            <!-- List Icon -->
			<list-icon v-if="getCustomerContactsInfo.length" class="svg-box" @click="modalIsOpen = true"/>
		</div>
		<phone-number-selector-modal
		v-if="modalIsOpen"
		:title="t('userArea.profile.selectPhone')"
		:rightButtonText="t('userArea.profile.save')"
		:contactList="getCustomerContactsInfo"
		@hideMe="modalIsOpen = false"
		@rightButtonClicked="saveHandler"
		@updatePhoneNumber="activeSelectedId = $event"/>
	</div>
    <!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Loader -->
    <loader-generic v-if="showOverlaySpinner" isOverlay/>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'
import ListIcon from '@/assets/icons/list.svg?inline'
import PhoneNumberSelectorModal from '@/components/features/userArea/profile/PhoneNumberSelectorModal'
import ToastGeneric from '@/components/generic/toasts/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'

/* Assets & Helpers */
import { ref, computed } from 'vue'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestChangeDefaultPhoneNumber } = mapActions(moduleMapper.USER_AREA_PROFILE)
const { getCustomerContactsInfo } = mapGetters(moduleMapper.USER_AREA_PROFILE)
const { updateCustomerImageUrl } = mapActions(moduleMapper.SESSION)

/** Props received:
@label { String }
*/
const props = defineProps({
    label: String
})

/* Data */
const modalIsOpen = ref(false)
const showOverlaySpinner = ref(false)

const activeSelectedId = ref(getCustomerContactsInfo.value
							? getCustomerContactsInfo.value.find((element) => { return element.default })
								? getCustomerContactsInfo.value.find((element) => { return element.default }).contact : null
							: null)

const selectorLabel = computed(() => {
    return props.label ? props.label : t('userArea.profile.phoneAssociated')
})

const selectedPhoneNumber = computed(() => {
    return getCustomerContactsInfo.value ? getCustomerContactsInfo.value.find((element) => { return element.default }) : null
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const saveHandler = async () => {
    showOverlaySpinner.value = true

    try {
        const response = await requestChangeDefaultPhoneNumber(activeSelectedId.value)
        successHandler(response.data)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, saveHandler)
	}
}

/*************************************************
*                                                *
*                 Toast Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    showOverlaySpinner.value = false
    modalIsOpen.value = false

	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showOverlaySpinner.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

</script>

<style lang="scss">
.phone-number-selector-wrapper {
	width: 100%;
	margin-bottom: 16px;
	position: relative;

	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;

	.selected-number-container {
		width: 100%;
		min-width: 320px;
		height: 30px;
        margin-top: 4px;

		display: flex;
        justify-content: space-between;
        align-items: center;

		&.remove-border {
			border-bottom: unset;
		}

		&.mobile {
			min-width: unset;
		}

		.selected-number {
			font-size: 14px;
			font-weight: 400;
			line-height: 18px;
			color: var(--secondary-dark-2);
            overflow: hidden;
		}

		svg {
			cursor:pointer;
		}
	}
}

</style>
